/**
 * "Somar dias à sua licença" (tela 7 da proposta, decisão de 15/10/2026: sem código, os dias
 * entram direto na licença da Máquina de Leads). Só os períodos que cabem no saldo ficam
 * liberados; o sugerido é o maior deles. A licença nova = a data atual da licença (ou hoje, se já
 * terminou) + o período, a mesma conta do backend.
 *
 * Movimento (23/10/2026): o ReferralDashboard mantém montado com usePresenca e passa `aberto`
 * (.ds-veu, .ds-dialogo). Ao escolher o período, o círculo fecha o anel (.ds-opcao), o contorno
 * do cartão acende (.ds-escolhivel) e a data nova rola no lugar (Rola, .ds-rola: sobe para um
 * período maior, desce para um menor). Confirmado, o ícone de sucesso entra com .ds-sucesso.
 * Enquanto sai, a janela continua montada e com o foco: "Confirmar" não vale mais.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useRedeemDays, type ReferralSummary } from '../../api/useReferral';
import { emitActivity } from '../../lib/activity';
import { Rola } from '../lists/Rola';
import { largestStop, REDEEM_STOPS } from './referralMath';
import { useReferralText } from './referralText';

const DAY_MS = 86_400_000;

interface RedeemDialogProps {
  summary: ReferralSummary;
  product: string;
  onClose: () => void;
  /** Movimento: janela aberta (.is-aberto); quem monta liga depois de montar e desliga ao sair. */
  aberto?: boolean;
}

export function RedeemDialog({
  summary,
  product,
  onClose,
  aberto = true,
}: RedeemDialogProps): JSX.Element {
  const { tx, days, date, period } = useReferralText();
  const redeem = useRedeemDays();
  const available = summary.balance.availableDays;
  const suggested = largestStop(available);
  const [selected, setSelected] = useState<number>(suggested || REDEEM_STOPS[0]);
  // Depois de aberta, `aberto` falso quer dizer que a janela está saindo (montada por 140 ms).
  const [abriu, setAbriu] = useState(aberto);
  if (aberto && !abriu) setAbriu(true);
  const saindo = abriu && !aberto;
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ days: number; expiredAt: string } | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const now = Date.now();
  const current = summary.license.expiredAt ? new Date(summary.license.expiredAt) : null;
  const ended = !current || current.getTime() < now;
  const base = ended ? now : current!.getTime();
  const newEnd = new Date(base + selected * DAY_MS);
  const fits = selected <= available;

  function confirm(): void {
    if (saindo || !fits || redeem.isPending) return;
    setError(null);
    redeem.mutate(selected, {
      onSuccess: (res) => {
        if (res.success && res.expiredAt) {
          emitActivity('referral.redeemed', { days: selected });
          setDone({ days: selected, expiredAt: res.expiredAt });
          return;
        }
        if (res.error === 'insufficient_balance') setError(tx('redeem_insufficient'));
        else if (res.error === 'lifetime') setError(tx('redeem_lifetime'));
        else setError(tx('redeem_error'));
      },
      onError: () => setError(tx('redeem_error')),
    });
  }

  return createPortal(
    <div
      className={`rf-overlay ds-veu${aberto ? ' is-aberto' : ''}`}
      role="presentation"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <section
        className={`rf-dialog ds-dialogo${aberto ? ' is-aberto' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="rf-redeem-title"
      >
        <div className="rf-dialog__head">
          <div>
            <span className="rf-eyebrow">
              <span className="material-symbols-rounded" aria-hidden="true">
                {done ? 'check_circle' : 'event_available'}
              </span>
              {done ? tx('done_eyebrow') : tx('redeem_eyebrow')}
            </span>
            <h2 id="rf-redeem-title">
              {done ? tx('done_title', { days: days(done.days) }) : tx('redeem_title')}
            </h2>
          </div>
          <button
            type="button"
            className="rf-dialog__close ds-resposta"
            aria-label={tx('close')}
            onClick={onClose}
          >
            <span className="material-symbols-rounded">close</span>
          </button>
        </div>

        {done ? (
          <div className="rf-done">
            <span className="rf-done__icon ds-sucesso" aria-hidden="true">
              <span className="material-symbols-rounded">event_available</span>
            </span>
            <p
              className="rf-dialog__text"
              dangerouslySetInnerHTML={{
                __html: tx('done_text', { product, date: date(done.expiredAt) }),
              }}
            />
            <button type="button" className="rf-btn rf-btn--dark ds-resposta" onClick={onClose}>
              {tx('close')}
            </button>
          </div>
        ) : (
          <>
            <p
              className="rf-dialog__text"
              dangerouslySetInnerHTML={{ __html: tx('redeem_intro', { days: days(available) }) }}
            />

            <div className="rf-fieldset" role="radiogroup" aria-labelledby="rf-period-legend">
              <span id="rf-period-legend" className="rf-fieldset__legend">
                {tx('period')}
              </span>
              <div className="rf-periods">
                {REDEEM_STOPS.map((stop) => {
                  const off = stop > available;
                  const isSuggested = stop === suggested;
                  return (
                    <button
                      key={stop}
                      type="button"
                      role="radio"
                      aria-checked={!off && stop === selected}
                      aria-disabled={off}
                      className={`rf-period ds-escolhivel${!off && stop === selected ? ' is-escolhido' : ''}${isSuggested ? ' rf-period--suggested' : ''}`}
                      onClick={() => !off && setSelected(stop)}
                    >
                      <span className="rf-period__radio ds-opcao" aria-hidden="true" />
                      <strong>{period(stop)}</strong>
                      <span>{stop >= 31 ? days(stop) : tx('per_use')}</span>
                      <em>
                        {off
                          ? tx('missing', { days: days(stop - available) })
                          : isSuggested
                            ? tx('suggested')
                            : tx('fits')}
                      </em>
                    </button>
                  );
                })}
              </div>
              {suggested ? (
                <span className="rf-dialog__small">
                  {tx('suggestion', { period: period(suggested) })}
                </span>
              ) : null}
            </div>

            <div className="rf-when">
              <span>
                <small>{tx(ended ? 'license_ended' : 'license_ends', { product })}</small>
                <strong>{current ? date(current) : '—'}</strong>
              </span>
              <span className="material-symbols-rounded" aria-hidden="true">
                arrow_forward
              </span>
              <span>
                <small>{tx('will_end')}</small>
                <strong className="rf-when__new">
                  <Rola valor={selected} texto={date(newEnd)} />
                </strong>
              </span>
            </div>

            {error ? (
              <div className="rf-alert" role="alert">
                <span className="material-symbols-rounded" aria-hidden="true">
                  error
                </span>
                {error}
              </div>
            ) : null}

            <div className="rf-dialog__buttons">
              <button
                type="button"
                className="rf-btn rf-btn--yellow ds-resposta"
                disabled={!fits || redeem.isPending}
                onClick={confirm}
              >
                <span className="material-symbols-rounded" aria-hidden="true">
                  add
                </span>
                {tx('confirm', { days: days(selected) })}
              </button>
              <button type="button" className="rf-btn rf-btn--ghost ds-resposta" onClick={onClose}>
                {tx('cancel')}
              </button>
            </div>
            <span className="rf-dialog__small">
              {tx('after', { days: days(selected), left: days(Math.max(0, available - selected)) })}
            </span>
          </>
        )}
      </section>
    </div>,
    document.body,
  );
}

